// schema
import * as ISchema from "@schema";
import { ApolloClient, NormalizedCacheObject } from "@apollo/client";
import { isEqual } from "lodash";
import moment from "moment-timezone";
import { ICalViewState } from "@reducers/chrome-extension.reducer";
import { getRenderTimeZone } from "@util/time-zone.util";
import { timeSlots } from "@constants/time-slots.constant";

// actions
import {
  setLinkName as setLinkNameAction,
  setSchedulingLinks as setSchedulingLinksAction,
} from "@actions/settings.actions";

// util
import { logger } from "@util/logger.util";

// queries
import { findOrCreateLinkNameMutation } from "@clockwise/web-commons/src/ui/scheduling-link/scheduling-link.graphql";
import {
  FindOrCreateLinkName,
  FindOrCreateLinkNameVariables,
} from "@clockwise/web-commons/src/ui/scheduling-link/__generated__/FindOrCreateLinkName";

//////////////////
// TYPES
//////////////////
// one-to-one with GraphQL input types for query
export interface IScoreNewEventInput {
  orgRelayId: string;
  timeZone: string;
  rangeMin: ISchema.IDayTimeSlotInput;
  rangeMax: ISchema.IDayTimeSlotInput;
  duration: number;
  roomNeeded: boolean;
  attendees: string[];
}

export enum EScheduleRange {
  NextTwo = "NextTwoDays",
  Today = "Today",
  Tomorrow = "Tomorrow",
  ThisWeek = "ThisWeek",
  NextSeven = "NextSevenDays",
  NextWeek = "NextWeek",
  CurrentView = "CurrentView",
  AroundDate = "AroundDate",
  SpecificDate = "SpecificDate",
  SevenBeforeAndAfter = "SevenBeforeAndAfter",
}

interface IMinMax {
  rangeMin: ISchema.IDayTimeSlotInput;
  rangeMax: ISchema.IDayTimeSlotInput;
}

//////////////////
// INTERNAL HELPERS
//////////////////
// generate 'YYYY-MM-DD' from calViewState
function generateDateString(calViewState: ICalViewState) {
  const { year, month, selectedDay } = calViewState;
  return `${year}-${month}-${selectedDay}`;
}

// create a min/max range
function createRangeOfDays(startDay: moment.Moment, numDays: number) {
  const rangeMin = {
    date: startDay.clone().format("YYYY-MM-DD"),
    timeSlot: timeSlots.all[0],
  };
  const rangeMax = {
    date: startDay.clone().add(numDays, "days").format("YYYY-MM-DD"),
    timeSlot: timeSlots.all[timeSlots.all.length - 1],
  };
  return { rangeMin, rangeMax };
}

// handles logic of what range is relevant for a given week
function createRangeForWeek(today: moment.Moment, selectedDate: moment.Moment) {
  const startOfSelectedWeek = selectedDate.clone().startOf("week");
  const startOfThisWeek = today.clone().startOf("week");
  const inFutureWeek = today.isBefore(selectedDate.clone().startOf("week"));

  if (inFutureWeek) {
    // if in future, just send the whole week
    return createRangeOfDays(startOfSelectedWeek, 7);
  }
  // if we're not in a future week, we're either in a past week or the current week
  // past week doesn't make much sense, so just treat it as today
  if (today.day() >= 4) {
    // if we're on Thursday or later, send down next 7 days
    return createRangeOfDays(today, 7);
  }
  // otherwise, send down the current week
  return createRangeOfDays(startOfThisWeek, 7);
}

// uses the state from google calendar to calculate date ranges
function getRangesFromCalViewState(calViewState: ICalViewState): IMinMax {
  const { activeView } = calViewState;
  const dateString = generateDateString(calViewState);
  const today = moment().tz(getRenderTimeZone());
  const selectedDate = moment.tz(dateString, "YYYY-MM-DD", getRenderTimeZone());

  switch (activeView) {
    case "agenda":
    case "week":
      return createRangeForWeek(today, selectedDate) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case "day":
      // send all 3 days
      return createRangeOfDays(selectedDate, 0) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case "month":
    case "year":
      // send selected day + 2 weeks
      return createRangeOfDays(selectedDate, 14) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case "custom":
      // send 4 days
      return createRangeOfDays(selectedDate, 4) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    default:
      // just return a range of 7 days starting from today
      return createRangeOfDays(today, 7) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
  }
}

function getMaxMinSlotForQuickScheduler(
  range: EScheduleRange,
  calViewState: ICalViewState,
): IMinMax {
  const now = moment().tz(getRenderTimeZone());

  // special handling if a calViewState is present
  if (range === EScheduleRange.CurrentView && !!calViewState) {
    return getRangesFromCalViewState(calViewState);
  }
  return getMaxMinSlotForRange(range, now);
}

export function getMaxMinSlotForRange(range: EScheduleRange, inputDate?: moment.Moment): IMinMax {
  const date = inputDate || moment();
  const isLate = date.hour() > 15; // TODO: use working hours
  let numDays = isLate ? 3 : 2;

  // otherwise switch on the range enum
  switch (range) {
    case EScheduleRange.Today:
    case EScheduleRange.SpecificDate:
      return createRangeOfDays(date, 0) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case EScheduleRange.Tomorrow:
      return createRangeOfDays(date.clone().add(1, "day"), 0) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case EScheduleRange.ThisWeek:
      numDays = 6 - date.day();
      return createRangeOfDays(date, numDays) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case EScheduleRange.NextWeek:
      numDays = 6 - date.day();
      return createRangeOfDays(date.add(1, "weeks").startOf("isoWeek"), 6) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case EScheduleRange.AroundDate:
      return createRangeOfDays(date.clone().subtract(1, "day"), 2) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case EScheduleRange.NextSeven:
      let addedDays = 7;
      if (date.day() === 0 || date.day() === 6) {
        // we're on Saturday or Sunday, add an extra day
        // arguably, Saturday should add 2 full days...
        addedDays = isLate ? addedDays + 2 : addedDays + 1;
      } else if (date.day() === 4 || date.day() === 5) {
        // we're on Thursday or Friday, so push through the weekend
        // Thursday +9 -> Sat
        // Friday +9 -> Sun
        addedDays += 2;
      }
      return createRangeOfDays(date, addedDays) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
    case EScheduleRange.SevenBeforeAndAfter:
      // Note that this makes a 14 day range with 7 days looking ahead
      return createRangeOfDays(date.subtract(6, "days"), 13) as any;
    case EScheduleRange.NextTwo:
    default:
      if (date.day() === 0 || date.day() === 6) {
        // we're on Saturday or Sunday, add an extra day
        // arguably, Saturday should add 2 full days...
        numDays++;
      } else if (date.day() === 4 || date.day() === 5) {
        // we're on Thursday or Friday, so push through the weekend
        numDays += 2;
      }
      return createRangeOfDays(date, numDays) as any; // FIXME: TEMP FIX FOR GQL2TS ISSUES
  }
}

// checks to see if you're far enough in the past such that this makes no sense
// not in use!
// function isValidCalViewToSchedule(
//   calViewState: ICalViewState | null,
// ): boolean {
//   if (!calViewState) {
//     return false;
//   }
//   const { activeView, year, month, selectedDay } = calViewState;
//   const dateString = `${year}-${month}-${selectedDay}`;

//   const selectedDate = moment.tz(dateString, 'YYYY-MM-DD', getRenderTimeZone());
//   const today = moment().tz(getRenderTimeZone());
//   switch (activeView) {
//     case 'agenda':
//     case 'week':
//       // within week
//       return selectedDate.isSameOrAfter(today.clone().startOf('week'));
//     case 'month':
//       // within month
//       return selectedDate.isSameOrAfter(today.clone().startOf('month'));
//     case 'year':
//       // within year
//       return selectedDate.isSameOrAfter(today.clone().startOf('year'));
//     case 'day':
//       // within 3 days
//       return selectedDate.isAfter(today.clone().subtract(3, 'days'));
//     case 'custom':
//       // within 4 days
//       return selectedDate.isAfter(today.clone().subtract(4, 'days'));
//     default:
//       return false;
//   }
// }

//////////////////
// EXPORTED UTIL
//////////////////
export function isCalViewInFutureWeek(calViewState: ICalViewState | null): boolean {
  if (!calViewState) {
    return false;
  }
  const dateString = generateDateString(calViewState);
  const today = moment().tz(getRenderTimeZone());
  const selectedDate = moment.tz(dateString, "YYYY-MM-DD", getRenderTimeZone());
  return today.isBefore(selectedDate.clone().startOf("week"));
}

// wraps everything together in a nice function that returns the variables needed for quick scheduler
export function genNewEventInputForQuickScheduler(
  orgId: string,
  calViewState: ICalViewState,
  duration: number, // desired new event length in minutes
  range: EScheduleRange, // the range we want results from
  roomNeeded: boolean, // if user wants a room
  calIds: string[], // calendar Ids of people to schedule with
  eventTimeZone?: string,
): IScoreNewEventInput {
  const timeZone = eventTimeZone ? eventTimeZone : getRenderTimeZone();
  const { rangeMax, rangeMin } = getMaxMinSlotForQuickScheduler(range, calViewState);
  return {
    duration,
    roomNeeded,
    timeZone,
    rangeMax,
    rangeMin,
    attendees: calIds,
    orgRelayId: orgId,
  };
}

export function genNewEventInputForRange(
  orgId: string,
  date: moment.Moment,
  duration: number, // desired new event length in minutes
  range: EScheduleRange, // the range we want results from
  roomNeeded: boolean, // if user wants a room
  calIds: string[], // calendar Ids of people to schedule with
  eventTimeZone?: string,
): IScoreNewEventInput {
  const timeZone = eventTimeZone ? eventTimeZone : getRenderTimeZone();
  const { rangeMax, rangeMin } = getMaxMinSlotForRange(range, date);
  return {
    duration,
    roomNeeded,
    timeZone,
    rangeMax,
    rangeMin,
    attendees: calIds,
    orgRelayId: orgId,
  };
}

export function slotsEqual(
  slot1: ISchema.ISchedulingSlot | undefined,
  slot2: ISchema.ISchedulingSlot | undefined,
) {
  if (!slot1 && !slot2) {
    return true;
  }

  if (!slot1 || !slot2) {
    return false;
  }

  const startTimeEqual = isEqual(
    slot1?.timeRange.startTime.dateOrDateTime,
    slot2?.timeRange.startTime.dateOrDateTime,
  );
  const endTimeEqual = isEqual(
    slot1?.timeRange.endTime.dateOrDateTime,
    slot2?.timeRange.endTime.dateOrDateTime,
  );
  const roomsEqual = isEqual(slot1?.roomImpacts?.[0]?.roomId, slot2?.roomImpacts?.[0]?.roomId);

  return startTimeEqual && endTimeEqual && roomsEqual;
}

export const makeLinkName = async (
  client: ApolloClient<NormalizedCacheObject>,
  setSchedulingLinks: typeof setSchedulingLinksAction,
  setLinkName: typeof setLinkNameAction,
) => {
  try {
    const linkNameRes = await client.mutate<FindOrCreateLinkName, FindOrCreateLinkNameVariables>({
      mutation: findOrCreateLinkNameMutation,
      variables: {
        input: {},
      },
    });

    const data = linkNameRes?.data?.findOrCreateLinkName;

    // Save user scheduling links to Redux
    if (data?.schedulingLinks) {
      setSchedulingLinks(data?.schedulingLinks || []);
    }

    // Save linkName to Redux
    if (data?.linkName) {
      setLinkName(data?.linkName);
    }

    return {
      linkName: data?.linkName || "",
      schedulingLinks: data?.schedulingLinks || [],
    };
  } catch (err) {
    logger.warn("failed to make link name", err);

    return Promise.reject("");
  }
};
